import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Notification } from "../../@types/Notification";
import {
  IPatientInfo,
  IPatientListVariants,
  IPortalPatientListItem,
  PatientListConfig,
  PatientListTableContent,
} from "../../@types/Patient";
import { IPortalPreferences } from "../../@types/Preferences";
import { Dots } from "../../assets/icons/Dots";
import { TableSettings } from "../../assets/icons/TableSettings";
import { AlertsCounter } from "../../components/AlertsCounter";
import { CustomIconButton } from "../../components/CustomIconButton";
import { Dropdown, DropdownTextItem } from "../../components/Dropdown";
import { DropdownRouter } from "../../components/DropdownRouter";
import { Pagination } from "../../components/Pagination";
import { ActionContainer } from "../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import { Tag } from "../../components/Tag";
import {
  AUTH_PORTAL_PREFERENCES,
  PATIENT_LIST_VARIANT,
  PATIENT_IDS,
  PATIENT_LIST_LAST_SELECTED_ID,
  PATIENT_LIST_LAST_SELECTED_PAGE,
  PATIENT_LIST_SORTING_STATE,
} from "../../constants/localStorageKeys";
import {
  getPatientListSortParams,
  isPageOutOfRange,
} from "../../constants/patientListSort";
import { patientListVariants } from "../../constants/patientListVariants";
import { invalidateAllNoteCaches } from "../../hooks/mutations/notes";
import {
  useFlagPatient,
  useResendOnboardingEmail,
} from "../../hooks/mutations/patients";
import { usePatientListColumns } from "../../hooks/queries/customFields";
import { usePatientList } from "../../hooks/queries/patients";
import {
  useApplicableRagMeasurements,
  useRagAvailableMeasurements,
} from "../../hooks/queries/ragConfig";
import { useArrowsListennerHandler } from "../../hooks/useArrowsListennerHandler";
import useIsMobile from "../../hooks/useIsMobile";
import { useModal } from "../../hooks/useModal";
import { useToast } from "../../hooks/useToast";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { PageLayout } from "../../layout/components/PageLayout";
import {
  MobilePageTitleContainer,
  PageTitle,
} from "../../layout/components/PageTitle";
import { reportService } from "../../services/reportService";
import {
  customColumnConfigKey,
  CUSTOM_COLUMN_CONFIG_PREFIX,
  formatCustomColumnValue,
} from "../../utils/customFields";
import {
  getRelativeTimeSinceDate,
  LONG_DATE_TIME_FORMAT,
} from "../../utils/dateFormatter";
import { mixpanelActions } from "../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../utils/storage";
import { CreateNoteModal } from "../PatientPage/components/PatientNotesTable/components/CreateNoteModal";
import { AddPatientModal } from "./components/AddPatientModal";
import { ArchivePatientModal } from "./components/ArchivePatientModal";
import { MyTasksTable } from "./components/MyTasksTable";
import { PatientListConfigModal } from "./components/PatientListConfigModal";
import { PatientListSortedTable } from "./components/PatientListSortedTable";
import { CustomFieldCell } from "./components/PatientListSortedTable/components/CustomFieldCell";
import { FlagPatientButton } from "./components/PatientListSortedTable/components/FlagPatientButton/FlagPatientButton";
import { RagIndicator } from "./components/PatientListSortedTable/components/RagIndicator";
import { defaultTableColumns } from "./components/PatientListSortedTable/tableColumns";
import {
  AddPatientButton,
  LayoutCss,
  PatientCountContainer,
  PatientListHeaderContainer,
  PatientListTableContainer,
  TableFadeWrapper,
  TableSettingsContainer,
  TagContainer,
} from "./PatientList.styles";

const PAGE_SIZE = 30;

// EN-5123: "RAG MEASUREMENTS (MAX 3)" — kept in step with PatientListConfigModal and, on the server,
// with PatientListColumnSet.MaxRagMeasurements.
const MAX_RAG_MEASUREMENTS = 3;

/**
 * EN-5123: the RAG measurement columns to render — the measurement codes the user enabled that a
 * published config applying to their wards actually bands, capped at 3.
 *
 * Deliberately narrow-then-cap, in the saved config's key order: the API resolves its
 * `ragMeasurements` the same way (PatientListColumnSetParser.NarrowRagMeasurements), so the columns
 * and the values always line up — including for a config saved before the cap existed, which can
 * have all seven vitals ticked with only one of them banded.
 */
function resolveEnabledRagCodes(
  config: PatientListConfig,
  applicableMeasurements: Set<string>
): string[] {
  return Object.keys(config)
    .filter((key) => config[key] === true && applicableMeasurements.has(key))
    .slice(0, MAX_RAG_MEASUREMENTS);
}

const DEFAULT_PATIENT_LIST_CONFIG: PatientListConfig = {
  name: true,
  hospitalId: true,
  pmpId: false,
  alerts: true,
  signUpDate: true,
  lastUsed: true,
  lastReviewedBy: true,
  carerEmail: false,
  surveyResults: undefined,
};

// EN-5067: read the saved column config synchronously on first render (like `page`/`sorting`),
// so the list's very first request already knows which custom columns are on. Deriving this
// asynchronously caused an initial request without `customFieldIds` (empty custom columns) whose
// stale result kept showing even after the correct request resolved.
function readInitialPatientListConfig(): PatientListConfig {
  const stored = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);
  const prefs = safeJsonParse<IPortalPreferences | undefined>(
    stored,
    undefined
  );

  if (prefs?.patientListConfig && prefs.patientListConfig !== "") {
    try {
      return JSON.parse(prefs.patientListConfig) as PatientListConfig;
    } catch {
      return { ...DEFAULT_PATIENT_LIST_CONFIG };
    }
  }

  if (prefs?.isBeaumont) {
    return { ...DEFAULT_PATIENT_LIST_CONFIG, surveyResults: true };
  }

  return { ...DEFAULT_PATIENT_LIST_CONFIG };
}

export function PatientListHeader({ variant }: IPatientListVariants) {
  const { isMobile } = useIsMobile();
  const { t } = useTranslation();

  useEffect(() => {
    if (variant === patientListVariants.ACTIVE) {
      mixpanelActions.track("Patient List Page: Active");
    } else {
      mixpanelActions.track("Patient List Page: Archived");
    }
  }, [variant]);

  if (isMobile) {
    return (
      <MobilePageTitleContainer>
        <PageTitle>{t("patientList_titleMobile")}</PageTitle>

        <DropdownRouter childRoutesOf="PatientList" />
      </MobilePageTitleContainer>
    );
  }

  return <PageTitle>{t(`patientList_title_${variant}`)}</PageTitle>;
}

export function PatientList({ variant }: IPatientListVariants) {
  const navigate = useNavigate();
  const { disablePatientListArrowNav, enablePatientListArrowNav } =
    useArrowsListennerHandler();
  const { isModalOpen } = useModal();
  const { showToast } = useToast();
  const { t } = useTranslation();

  // One key per variant, like the heading a few lines below. Interpolating the route variant
  // put an untranslated English word inside a translated sentence — the Dutch tab read
  // "Active patiënten - patientMpower".
  document.title = t(`patientList_documentTitle_${variant}`);

  // Restore the page and sort the user was on when they clicked into a
  // patient — saved to sessionStorage by the row click handler. Read once
  // on mount and consume the page key so a future un-related navigation
  // back to the list doesn't reuse a stale page.
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem(PATIENT_LIST_LAST_SELECTED_PAGE);
    if (!saved) return 1;
    sessionStorage.removeItem(PATIENT_LIST_LAST_SELECTED_PAGE);
    const parsed = Number.parseInt(saved, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  });
  const [sorting, setSorting] = useState<SortingState>(() => {
    // With manualSorting we own the state, so TableSorted's own
    // sessionStorage restore doesn't run — restore here instead.
    const saved = sessionStorage.getItem(PATIENT_LIST_SORTING_STATE);
    if (!saved) return [];
    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? (parsed as SortingState) : [];
    } catch {
      return [];
    }
  });

  // EN-5067: the user's chosen column on/off state, hydrated synchronously from localStorage so
  // the first list request already carries the right customFieldIds.
  const [patientListConfig, setPatientListConfig] = useState<PatientListConfig>(
    readInitialPatientListConfig
  );
  const { customColumns } = usePatientListColumns();

  const { applicableMeasurements } = useApplicableRagMeasurements();
  // Display names and units for the RAG column headers.
  const { byCode: ragMeasurementsByCode } = useRagAvailableMeasurements();

  // EN-5123: the RAG columns the user has on, which is also the set the backend will accept as a
  // measurement sort key.
  const enabledRagCodes = useMemo(
    () => resolveEnabledRagCodes(patientListConfig, applicableMeasurements),
    [patientListConfig, applicableMeasurements]
  );

  // Derive the backend sort from the table's sort state. Shared with the
  // patient-page navigation arrows so both sort by the same keys.
  const { sortBy, sortDir } = getPatientListSortParams(
    sorting,
    enabledRagCodes
  );

  const customColumnById = useMemo(
    () =>
      new Map(
        customColumns.map((column) => [
          customColumnConfigKey(column.customFieldDefinitionId),
          column,
        ])
      ),
    [customColumns]
  );

  // EN-5067: the enabled custom columns' ids come straight from the config keys (`custom:{id}`),
  // NOT from the async columns endpoint — so `customFieldIds` is known on the first render and the
  // list fires a single request with values already included. Omitted entirely when none are on.
  const customFieldIds = useMemo(() => {
    const ids = Object.keys(patientListConfig)
      .filter(
        (key) =>
          key.startsWith(CUSTOM_COLUMN_CONFIG_PREFIX) && patientListConfig[key]
      )
      .map((key) => key.slice(CUSTOM_COLUMN_CONFIG_PREFIX.length));
    return ids.length > 0 ? ids.join(",") : undefined;
  }, [patientListConfig]);

  // Guard against sorting by a custom column that isn't loaded yet or isn't sortable (e.g. a stale
  // sort restored from a previous session) — the backend would 400. Fall back to the default order.
  const isCustomSortValid =
    !sortBy?.startsWith("custom:") ||
    (customColumnById.get(sortBy)?.sortable ?? false);
  const effectiveSortBy = isCustomSortValid ? sortBy : undefined;
  const effectiveSortDir = isCustomSortValid ? sortDir : undefined;

  // The wards picked in the header. Joined so the reset below fires on a
  // real change, not on every new preferences object.
  const { portalPreferences, changePortalPreferences } = useUserPreferences();
  const selectedWardsKey = (portalPreferences.wards ?? []).join(",");

  // Reset to page 1 when sort, variant or the selected wards change
  // (otherwise the user could be on page 4 of an old result set with no row
  // 4 in the new one). Skip the first run so the page restored from
  // sessionStorage isn't clobbered immediately on mount.
  //
  // EN-5277: the wards were missing here. Narrowing from 38 patients on
  // page 2 to a ward of 7 left the list on page 2 — "Active patients (7)"
  // over an empty table, and no pagination to get back.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPage(1);
  }, [sortBy, sortDir, variant, selectedWardsKey]);

  const { patients, totalCount, isLoading, isFetching, refetch } =
    usePatientList(
      { variant },
      {
        page,
        pageSize: PAGE_SIZE,
        sortBy: effectiveSortBy,
        sortDir: effectiveSortDir,
        customFieldIds,
      }
    );

  // A page past the end can still be reached — restored from sessionStorage
  // after the list shrank, or the wards changed elsewhere. Fall back to page 1
  // rather than strand the user on an empty table (EN-5277).
  useEffect(() => {
    if (isFetching) return;
    if (isPageOutOfRange(page, totalCount, PAGE_SIZE)) setPage(1);
  }, [isFetching, page, totalCount]);

  const [highlightRowId, setHighlightRowId] = useState<number | null>(() => {
    const savedId = sessionStorage.getItem(PATIENT_LIST_LAST_SELECTED_ID);
    return savedId ? Number(savedId) : null;
  });

  // Consume the stored ID after mount so it won't persist on future navigations
  useEffect(() => {
    sessionStorage.removeItem(PATIENT_LIST_LAST_SELECTED_ID);
  }, []);

  const handleHighlightClear = useCallback(() => {
    setHighlightRowId(null);
  }, []);

  const handleDownloadDischargeSummary = useCallback(
    async (patientId: number) => {
      showToast(t("patientList_toasts_dischargeStarted"), "warning");

      try {
        const result = await reportService.downloadDischargeSummaryReport(
          patientId
        );

        if (result.status < 200 || result.status >= 300) {
          showToast(t("patientList_toasts_dischargeFailed"), "error");
          return;
        }

        const contentDisposition = result.headers["content-disposition"];
        const fileName =
          contentDisposition?.match(/filename\*=UTF-8''(.+)/)?.[1] ||
          contentDisposition?.match(/filename=(.+);?/)?.[1] ||
          `discharge-summary-${patientId}.pdf`;

        const url = URL.createObjectURL(new Blob([result.data]));
        const link = Object.assign(document.createElement("a"), {
          href: url,
          download: fileName,
        });
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);

        showToast(t("patientList_toasts_dischargeDownloaded"), "success");
      } catch {
        showToast(t("patientList_toasts_dischargeFailed"), "error");
      }
    },
    [showToast, t]
  );

  const mutate = useFlagPatient();
  const { mutateAsync: resendOnboardingEmail } = useResendOnboardingEmail();
  const [resendingIds, setResendingIds] = useState<Set<number>>(new Set());
  const queryClient = useQueryClient();

  const { openModal, closeModal } = useModal();

  const [tableColumns, setTableColumns] =
    useState<ColumnDef<any, any>[]>(defaultTableColumns);

  const handleOnModalClose = useCallback(
    (notification?: Notification) => {
      if (notification !== undefined) {
        refetch();
        showToast(notification.message, notification.type);
      }

      closeModal();
    },
    [refetch, showToast, closeModal]
  );

  const parsePatientsToTable = useMemo(() => {
    if (patients === undefined) return [];

    const handleTagVariant = (value: string) => {
      if (value === "No symptoms") {
        return "success";
      }

      if (value === "Symptoms") {
        return "tertiary";
      }

      return "error";
    };

    const patientIds: number[] = [];

    const getLasUsedDate = (
      lastUsed: string | null | undefined
    ): number | undefined => {
      if (!lastUsed || lastUsed === "-") return undefined;
      // Use the same format string getRelativeTimeSinceDate uses for display
      // so sort and display agree on whether the value is parseable.
      const parsed = dayjs(lastUsed, LONG_DATE_TIME_FORMAT);
      // Return undefined (not null) for missing values so react-table's
      // sortUndefined: "last" pins them to the end in both asc and desc.
      return parsed.isValid() ? parsed.valueOf() : undefined;
    };

    const patientList = patients?.map((patient: IPortalPatientListItem) => {
      patientIds.push(patient.id);
      let lastUsedContent = "";

      // EN-5191: an option's identity is its `id`; `label` is only what the operator reads.
      // Before this the label WAS the identity (the handler compared against the English
      // text), so translating the menu would have silently stopped every action working.
      const options: { id: string; label: string }[] = [
        variant === "archived"
          ? { id: "reactivate", label: t("patientList_actions_reactivate") }
          : { id: "archive", label: t("patientList_actions_archive") },
        { id: "openInNewTab", label: t("patientList_actions_openInNewTab") },
      ];

      if (variant !== "archived") {
        options.push({
          id: "resendOnboardingEmail",
          label: t("patientList_actions_resendOnboardingEmail"),
        });
      }

      if (variant === "archived") {
        options.push({
          id: "downloadDischargeDocs",
          label: t("patientList_actions_downloadDischargeDocs"),
        });
      }

      const handleArchivePatient = (isReactivate = false) => {
        openModal(
          <ArchivePatientModal
            content={patient}
            isReactivate={isReactivate}
            onClose={handleOnModalClose}
          />,
          {
            width: "623px",
            height: "100%",
            showCloseButton: true,
          }
        );
      };
      if (patient.lastUsed && patient.lastUsed !== "-") {
        lastUsedContent = getRelativeTimeSinceDate(patient.lastUsed);
      } else {
        lastUsedContent = t("patientList_noDataAdded");
      }

      const handleFlagPatient = async (patientId: number, flagged: boolean) => {
        await mutate({
          patientId,
          flagged,
        });

        // Optimistic update across both regular and infinite caches sharing
        // the `["patient-list-active", "active", …]` prefix. Infinite query
        // data has a `pages: [{ result: [...] }]` shape; the legacy regular
        // query is a flat array — handle both.
        queryClient.setQueriesData(
          { queryKey: ["patient-list-active", "active"] },
          (oldData: unknown) => {
            if (!oldData) return oldData;
            const updateItem = (p: IPortalPatientListItem) =>
              p.id === patientId ? { ...p, flagged } : p;
            if (Array.isArray(oldData)) {
              return (oldData as IPortalPatientListItem[]).map(updateItem);
            }
            const infinite = oldData as {
              pages?: { result: IPortalPatientListItem[] }[];
            };
            if (infinite.pages) {
              return {
                ...infinite,
                pages: infinite.pages.map((page) => ({
                  ...page,
                  result: page.result.map(updateItem),
                })),
              };
            }
            return oldData;
          }
        );

        queryClient.setQueryData(
          [`patient-info-${patientId}`, patientId.toString()],
          (patientInfo?: IPatientInfo) => {
            if (patientInfo) {
              return { ...patientInfo, flagged };
            }
            return patientInfo;
          }
        );
      };

      // Parses the alerts column for sort. Backend can return:
      //  - null / "0" → no alerts (sort as 0)
      //  - "5", "9+" → numeric counts (sort by leading digits)
      //  - "Workflow" or other label → a single special alert (sort as 1)
      const getAlertsCount = (alerts: string | null): number => {
        if (alerts == null) return 0;
        const trimmed = String(alerts).trim();
        if (trimmed === "" || trimmed === "0") return 0;
        const match = trimmed.match(/^(\d+)/);
        if (match) {
          const n = Number.parseInt(match[1], 10);
          return Number.isFinite(n) ? n : 1;
        }
        return 1;
      };
      return {
        id: patient.id,
        name: `${patient.firstName} ${patient.lastName}`,
        lastName: patient.lastName,
        firstName: patient.firstName,
        flagged: patient.flagged,
        lastUsed: lastUsedContent,
        lastUsedDate: getLasUsedDate(patient.lastUsed),
        hospitalId: patient.hospitalPatientId ?? "",
        signUpDate: patient.signUpDate,
        signUpdateParsedDate: dayjs(
          patient.signUpDate,
          LONG_DATE_TIME_FORMAT
        ).toDate(),
        lastReviewedBy: patient.lastReviewedBy,
        carerEmail: patient.hasCarerEmail ? t("common_yes") : t("common_no"),
        alertsCount: getAlertsCount(patient.alerts),
        alerts:
          patient.alerts !== null && patient.alerts !== "0" ? (
            <Tooltip
              mouseLeaveDelay={0}
              title={t("patientList_goToAlerts")}
              zIndex={3002}
              color="#4B4B4B"
              arrow={false}
              styles={{
                body: {
                  minHeight: "40px",
                  borderRadius: "4px !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                },
              }}
            >
              <CustomIconButton
                style={{
                  width: "max-content",
                  margin: "0 10px 0 0",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/patients/${patient.id}/alerts`);
                }}
              >
                <AlertsCounter>{patient.alerts}</AlertsCounter>
              </CustomIconButton>
            </Tooltip>
          ) : (
            "-"
          ),
        surveyReceived: patient.surveyReceived ?? "-",
        surveyReceivedDate: patient.surveyReceived
          ? dayjs(patient.surveyReceived, LONG_DATE_TIME_FORMAT).toDate()
          : 0,
        surveyResult: patient.surveyResult ? (
          <TagContainer>
            <Tag variant={handleTagVariant(patient.surveyResult)}>
              {patient.surveyResult}
            </Tag>
          </TagContainer>
        ) : null,
        surveyResultStatus:
          patient.surveyResult === null || patient.surveyResult === undefined
            ? ""
            : patient.surveyResult,
        // EN-5123: code-keyed RAG measurements, read by the dynamic RAG columns' cells.
        ragMeasurements: patient.ragMeasurements ?? {},
        // EN-5067: raw custom-field values, read by the dynamic custom columns' cells.
        customFields: patient.customFields ?? [],
        flag: (
          <div>
            <FlagPatientButton
              onClick={async (e) => {
                e.stopPropagation();

                await handleFlagPatient(patient.id, !patient.flagged);
              }}
              flagged={patient.flagged}
            />
          </div>
        ),
        action: (
          <div>
            <ActionContainer
              className="actionContainer"
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown
                trigger={
                  <CustomIconButton>
                    <Dots />
                  </CustomIconButton>
                }
                value=""
                onValueChange={(val: string) => {
                  if (val === options[0].id) {
                    if (variant === "archived") {
                      mixpanelActions.track(
                        "User Action: ReactivatePatientButton"
                      );
                      handleArchivePatient(true);
                    } else {
                      mixpanelActions.track(
                        "User Action: ArchivePatientButton"
                      );
                      handleArchivePatient();
                    }
                  }
                  if (val === options[1].id) {
                    mixpanelActions.track("User Action: OpenInNewTabButton");

                    window.open(
                      `/patients/${patient.id}/${
                        variant === patientListVariants.ALERTS
                          ? "alerts"
                          : "spirometry"
                      }`,
                      "_blank"
                    );
                  }
                  if (val === "downloadDischargeDocs") {
                    mixpanelActions.track(
                      "User Action: DownloadDischargeSummaryButton"
                    );
                    handleDownloadDischargeSummary(patient.id);
                  }
                  if (val === "resendOnboardingEmail") {
                    if (resendingIds.has(patient.id)) return;
                    mixpanelActions.track(
                      "User Action: ResendOnboardingEmailButton"
                    );
                    setResendingIds((prev) => new Set(prev).add(patient.id));
                    resendOnboardingEmail(patient.id)
                      .then(() => {
                        showToast(
                          t("patientList_toasts_onboardingEmailResent"),
                          "success"
                        );
                      })
                      .catch(() => {
                        showToast(
                          t("patientList_toasts_onboardingEmailFailed"),
                          "error"
                        );
                      })
                      .finally(() => {
                        setResendingIds((prev) => {
                          const next = new Set(prev);
                          next.delete(patient.id);
                          return next;
                        });
                      });
                  }
                }}
              >
                {options.map((option) => (
                  <DropdownTextItem
                    key={option.id}
                    value={option.id}
                    text={option.label}
                    disabled={
                      option.id === "resendOnboardingEmail" &&
                      resendingIds.has(patient.id)
                    }
                  />
                ))}
              </Dropdown>
            </ActionContainer>
          </div>
        ),
      } as unknown as PatientListTableContent;
    });

    // Update the patients IDs list to use for patient navigation
    safeStorage.setItem(PATIENT_IDS, JSON.stringify(patientIds));

    return patientList;
  }, [
    patients,
    variant,
    openModal,
    handleOnModalClose,
    mutate,
    resendOnboardingEmail,
    resendingIds,
    showToast,
    queryClient,
    navigate,
    handleDownloadDischargeSummary,
    t,
  ]);

  const handleColumns = (
    tableConfig: PatientListConfig,
    persist: boolean = false
  ) => {
    const columnsToAdd: ColumnDef<any, any>[] = [];
    const temporaryTableConfig = { ...tableConfig };

    if (temporaryTableConfig.name) {
      columnsToAdd.push({
        accessorKey: "name",
        header: () => t("patientList_columns_name"),
        cell: (info) => info.renderValue(),
        size: 220,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.lastName ?? "";
          const b = rowB.original.lastName ?? "";
          return a.localeCompare(b);
        },
      });
    }

    if (temporaryTableConfig.hospitalId) {
      columnsToAdd.push({
        accessorKey: "hospitalId",
        header: () => t("patientList_columns_hospitalId"),
        cell: (info) => info.renderValue(),
        sortingFn: "text",
      });
    }

    if (temporaryTableConfig.pmpId) {
      columnsToAdd.push({
        accessorKey: "id",
        header: () => t("patientList_columns_pmpId"),
        cell: (info) => info.renderValue(),
        sortingFn: "basic",
      });
    }

    if (temporaryTableConfig.alerts) {
      columnsToAdd.push({
        accessorKey: "alerts",
        header: () => t("patientList_columns_alerts"),
        cell: (info) => info.renderValue(),
        sortingFn: (rowA, rowB) => {
          return rowA.original.alertsCount - rowB.original.alertsCount;
        },
      });
    }

    if (temporaryTableConfig.signUpDate) {
      columnsToAdd.push({
        accessorKey: "signUpDate",
        header: () => t("patientList_columns_signUpDate"),
        cell: (info) => info.renderValue(),
        size: 160,
        sortingFn: (rowA, rowB) => {
          return (
            rowB.original.signUpdateParsedDate -
            rowA.original.signUpdateParsedDate
          );
        },
      });
    }

    if (temporaryTableConfig.lastUsed) {
      columnsToAdd.push({
        // accessorFn returns the timestamp so sortUndefined: "last" actually
        // fires for missing rows (accessorKey would return the display string,
        // which is never undefined and would defeat the check).
        id: "lastUsed",
        accessorFn: (row: { lastUsedDate?: number }) => row.lastUsedDate,
        header: () => t("patientList_columns_lastUsed"),
        cell: ({ row }) => (row.original as { lastUsed: string }).lastUsed,
        size: 160,
        sortingFn: (rowA, rowB) =>
          (rowB.getValue<number>("lastUsed") ?? 0) -
          (rowA.getValue<number>("lastUsed") ?? 0),
        sortUndefined: "last",
      });
    }

    if (temporaryTableConfig.lastReviewedBy) {
      columnsToAdd.push({
        id: "lastReviewedBy",
        accessorFn: (row: { lastReviewedBy?: string }) =>
          row.lastReviewedBy || undefined,
        header: () => t("patientList_columns_lastReviewedBy"),
        cell: (info) => info.renderValue(),
        sortingFn: "text",
        sortUndefined: "last",
      });
    }

    if (temporaryTableConfig.carerEmail) {
      columnsToAdd.push({
        accessorKey: "carerEmail",
        header: () => t("patientList_columns_carerEmail"),
        cell: (info) => info.renderValue(),
        enableSorting: false,
      });
    }

    if (temporaryTableConfig.surveyResults) {
      columnsToAdd.push(
        {
          accessorKey: "surveyReceived",
          header: () => t("patientList_columns_surveyReceived"),
          cell: (info) => info.renderValue(),
          size: 160,
          sortingFn: (rowA, rowB) => {
            return (
              rowA.original.surveyReceivedDate -
              rowB.original.surveyReceivedDate
            );
          },
        },

        {
          accessorKey: "surveyResult",
          header: () => t("patientList_columns_surveyResult"),
          cell: (info) => info.renderValue(),
          sortingFn: (rowA, rowB) => {
            return rowA.original.surveyResultStatus.localeCompare(
              rowB.original.surveyResultStatus
            );
          },
        }
      );
    }

    // EN-5123: the RAG columns are whatever the user has enabled that is also banded by a published
    // config applying to their wards — capped at 3 ("RAG MEASUREMENTS (MAX 3)"), in the order the
    // saved config lists them so the header order matches the modal. The column id is the
    // measurement code, which is both the response key and the backend sort key.
    resolveEnabledRagCodes(
      temporaryTableConfig,
      applicableMeasurements
    ).forEach((code) => {
      const meta = ragMeasurementsByCode.get(code);
      const name = meta?.measurementTypeDisplayName || code;
      const header = meta?.unit ? `${name} (${meta.unit})` : name;

      columnsToAdd.push({
        id: code,
        accessorFn: (row: PatientListTableContent) =>
          row.ragMeasurements?.[code]?.value ?? undefined,
        header: () => header,
        // RagIndicator renders "-" for a null/absent measurement, which is exactly what a patient
        // whose wards don't collect this measurement gets from the API.
        cell: ({ row }) => (
          <RagIndicator measurement={row.original.ragMeasurements?.[code]} />
        ),
        size: 120,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.ragMeasurements?.[code]?.value ?? -Infinity;
          const b = rowB.original.ragMeasurements?.[code]?.value ?? -Infinity;
          return a - b;
        },
        sortUndefined: "last",
      });
    });

    // EN-5067: append the enabled dynamic custom-field columns (in displayOrder from the API).
    customColumns.forEach((column) => {
      const key = customColumnConfigKey(column.customFieldDefinitionId);
      if (!temporaryTableConfig[key]) return;

      columnsToAdd.push({
        id: key,
        // accessorFn is required: TableSorted renders "-" (and skips the cell) when a cell's
        // accessor value is empty. Return the flat display value so a filled cell renders its
        // `cell` (text/chips) and an empty one falls back to "-".
        accessorFn: (row: PatientListTableContent) => {
          const match = row.customFields?.find(
            (value) =>
              value.customFieldDefinitionId === column.customFieldDefinitionId
          );
          if (!match) return "";
          return column.type === "Multiselect"
            ? (match.selectedOptions ?? []).join(", ")
            : formatCustomColumnValue(column.type, match);
        },
        header: () => column.label,
        cell: ({ row }) => (
          <CustomFieldCell
            column={column}
            values={(row.original as PatientListTableContent).customFields}
          />
        ),
        size: 160,
        // Server-side sort (manualSorting); the header toggle is disabled for non-sortable
        // columns (Multiselect). The backend sorts by the `custom:{id}` key.
        enableSorting: column.sortable,
        sortUndefined: "last",
      });
    });

    columnsToAdd.push({
      accessorKey: "flag",
      header: () => "",
      cell: (info) => info.renderValue(),
      size: 30,
      enableSorting: false,
    });

    columnsToAdd.push({
      accessorKey: "action",
      header: () => "",
      cell: (info) => info.renderValue(),
      size: 30,
      enableSorting: false,
    });

    setTableColumns(columnsToAdd);

    if (persist) {
      setPatientListConfig(temporaryTableConfig);

      const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);

      if (portalPreferences) {
        const parsedPortalPreferences = safeJsonParse<
          IPortalPreferences | undefined
        >(portalPreferences, undefined);

        if (parsedPortalPreferences) {
          const configJson = JSON.stringify(temporaryTableConfig);
          parsedPortalPreferences.patientListConfig = configJson;

          const portalPreferencesJson = JSON.stringify(parsedPortalPreferences);
          safeStorage.setItem(AUTH_PORTAL_PREFERENCES, portalPreferencesJson);
          changePortalPreferences(parsedPortalPreferences);
        }
      }
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      disablePatientListArrowNav();
    } else {
      enablePatientListArrowNav();
    }
  }, [isModalOpen]);

  // Re-derive columns when the set of applicable RAG measurements changes
  // (flag toggled off / rules deleted / rules added) so the table auto-hides
  // or reveals RAG columns without requiring the user to reopen the config.
  // `patientListConfig` is also a dep so that on remount this effect re-runs
  // after the mount-effect's `setPatientListConfig(parsed)` propagates,
  // instead of running once with the stale initial state and overwriting
  // the column update from the mount effect. We compare the applicable set
  // by *content* (sorted join) instead of identity, because the hook
  // returns a new `Set` reference on every detail-query refetch — without
  // this, harmless background refetches re-derived the columns.
  const applicableMeasurementsKey = useMemo(
    () => Array.from(applicableMeasurements).sort().join(","),
    [applicableMeasurements]
  );
  useEffect(() => {
    handleColumns(patientListConfig);
    // EN-5067: `customColumns` is a dep so columns rebuild once the custom-field definitions load.
  }, [applicableMeasurementsKey, patientListConfig, customColumns, t]);

  const handleOnPatientListConfigClose = async (
    tableConfig: PatientListConfig,
    notification?: Notification,
    refreshTable?: boolean
  ) => {
    closeModal();

    if (refreshTable) {
      if (tableConfig) {
        handleColumns(tableConfig, true);
      }
    }

    if (notification !== undefined) {
      showToast(notification.message, notification.type);
    }
  };

  const handleAddPatientClick = () => {
    openModal(<AddPatientModal onClose={handleOnModalClose} />, {
      maxHeight: "97vh",
      width: "100%",
      showCloseButton: true,
    });
  };

  const handleTableConfigClick = () => {
    mixpanelActions.track("User Action: ConfigurePatientList");

    openModal(
      <PatientListConfigModal
        columnsConfig={patientListConfig}
        customColumns={customColumns}
        onClose={handleOnPatientListConfigClose}
      />,
      {
        width: "655px",
        maxHeight: "628px",
        showCloseButton: true,
      }
    );
  };

  useEffect(() => {
    safeStorage.setItem(PATIENT_LIST_VARIANT, variant);
  }, [variant]);

  return (
    <PageLayout className={LayoutCss()} onClick={handleHighlightClear}>
      <PatientListHeaderContainer>
        <PatientListHeader variant={variant} />
        {!isLoading && (
          <>
            <PatientCountContainer>({totalCount})</PatientCountContainer>

            <Tooltip
              title={t("patientList_configureColumns")}
              arrow={false}
              placement="right"
              styles={{
                body: {
                  padding: "6px 12px",
                  minHeight: "40px",
                  width: "max-content",
                  minWidth: "220px",
                  borderRadius: "4px !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'Open Sans', sans-serif",
                },
              }}
            >
              <TableSettingsContainer
                role="button"
                tabIndex={0}
                onClick={handleTableConfigClick}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleTableConfigClick();
                  }
                }}
              >
                <TableSettings />
              </TableSettingsContainer>
            </Tooltip>
          </>
        )}
        <AddPatientButton onClick={handleAddPatientClick}>
          {t("patientList_addPatient")}
        </AddPatientButton>
      </PatientListHeaderContainer>

      <PatientListTableContainer>
        <TableFadeWrapper isFetching={!isLoading && isFetching}>
          <PatientListSortedTable
            variant={variant}
            isLoading={isLoading}
            content={parsePatientsToTable}
            columns={tableColumns}
            highlightRowId={highlightRowId}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting
            currentPage={page}
          />
        </TableFadeWrapper>
        {!isLoading && totalCount > 0 && (
          <Pagination
            currentPage={page}
            pageSize={PAGE_SIZE}
            totalItems={totalCount}
            onPageChange={setPage}
          />
        )}
      </PatientListTableContainer>

      {variant === patientListVariants.ALERTS && (
        <MyTasksTable
          onAddTaskClick={() =>
            openModal(
              <CreateNoteModal
                forceTaskMode
                setNotification={(message, type) => showToast(message, type)}
                onClose={() => {
                  // Invalidate every notes/tasks cache so the new task shows
                  // up in My Tasks AND in the patient page note list (which
                  // would otherwise stay stale until a hard refresh — the
                  // global QueryClient has refetchOnMount: false).
                  invalidateAllNoteCaches(queryClient);
                  closeModal();
                }}
              />,
              {
                width: "auto",
                height: "auto",
                showCloseButton: true,
              }
            )
          }
        />
      )}
    </PageLayout>
  );
}
